import React, { ChangeEvent, useState } from "react";
import { Dialog, DialogActions, DialogContent, Button, TextField, Stack, Typography } from "@mui/material";

interface FeedbackPopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
}

const validateComment = (value: string): string | undefined => {
  if (!value) {
    return "Comment is required.";
  } else if (value.length < 25) {
    return "Comment must be at least 25 characters long.";
  } else if (value.length > 5000) {
    return "Character limit should not exceed 5000.";
  }
  return undefined;
};

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ open, onClose, onSubmit }) => {
  const [comment, setComment] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [touched, setTouched] = useState(false);

  const resetState = () => {
    setComment("");
    setErrorMessage(undefined);
    setTouched(false);
  };

  const handleSubmit = () => {
    onSubmit(comment);
    resetState();
    onClose();
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const value = e.target.value;
    setComment(value);
    if (touched) {
      setErrorMessage(validateComment(value));
    }
  };

  const handleBlur = () => {
    setTouched(true);
    setErrorMessage(validateComment(comment));
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  return (
    <Dialog
      aria-labelledby="confirm-dialog-title"
      open={open}
      onClose={handleClose}
      maxWidth={"sm"}
      fullWidth
      slotProps={{
        paper: {
          variant: "popup",
          sx: {
            padding: 0,
          },
        },
      }}
    >
      <DialogContent
        sx={{
          paddingTop: 3,
          paddingBottom: 2,
        }}
      >
        <Stack sx={{ alignItems: "center" }}>
          <Stack spacing={2} sx={{ marginBottom: 2, width: "100%" }}>
            <Typography
              id="confirm-dialog-title"
              variant="h5"
              sx={{
                fontWeight: "fontWeightSemiBold",
                textAlign: "center",
                color: "primary.main",
              }}
            >
              Submit Feedback
            </Typography>
          </Stack>
        </Stack>

        <Stack sx={{ alignItems: "center", width: "100%" }}>
          <Stack spacing={2} sx={{ width: "100%", pt: 2 }}>
            <TextField
              label="Comment"
              required
              fullWidth
              multiline
              rows={4}
              value={comment}
              onChange={(e) => handleChange(e)}
              onBlur={handleBlur}
              margin="normal"
              helperText={errorMessage}
              error={!!errorMessage}
              slotProps={{
                htmlInput: { maxLength: 5000, minLength: 25 },
              }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "none",
          justifyContent: "center",
          padding: 3,
        }}
      >
        <Button id="cancel-button" variant="text" onClick={handleClose} sx={{ px: 4 }}>
          Cancel
        </Button>
        <Button
          id="confirm-button"
          color={"primary"}
          variant="contained"
          autoFocus
          onClick={handleSubmit}
          disabled={!!validateComment(comment)}
          sx={{ px: 4 }}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeedbackPopup;
